import { BillingClosureBadges } from "../../components/BillingClosureBadges";
import { billingClosureState } from "../../utils/billingClosureState";
import { adempimentiClosed } from "../../utils/adempimentiClosure";
import useAdempimentiRealtime from "../../hooks/useAdempimentiRealtime";
import { getMetodoFatturazioneListino, getIipayFasciaLabel, isIipayVoceAFascia } from "../../utils/iipayFascia";
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  itIT,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid-pro";
import { PersistentDataGridPro as DataGridPro } from "../../components/DataGrid/PersistentDataGridPro";
import LinearProgress from "@mui/material/LinearProgress";
import Modal from "../../components/Modal";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Autocomplete } from "../../components/Autocomplete";
import api from "../../api/api";
import { toast } from "react-toastify";
import { HiMiniArrowDownTray, HiOutlineCheck, HiPlus } from "react-icons/hi2";
import { MdCheck, MdClose, MdEdit, MdThumbUp, MdThumbDown } from "react-icons/md";
import { knowAcces } from "../../utils/utils";
import { formatRichiestaDescrizioneLabel } from "../../utils/formatRichiestaDescrizioneLabel";
import { EXPORT_BUTTON_CLASS } from "../../utils/exportButtonStyle";
import { GridVisibleRowsCount } from "../../components/DataGrid/CustomFooterPersonalized";
import { TotalSectionCard } from "../../components/TotalSectionCard";
import { formatItalianNumber } from "../../utils/formatItalianNumber";
import * as XLSX from "xlsx-js-style";
import { BPOINT_1427_TYPES, calculateBpoint1427, isIntegrazioneSalariale, isZucchetti1237 } from "../../utils/bpoint1427";
import { BPOINT_1439_BANDS, calculateBpoint1439, isBpoint1439 } from "../../utils/bpoint1439";

const STATO_COLORS = {
  bozza:     "bg-gray-100 text-gray-700",
  validata:  "bg-green-100 text-green-700",
  forzata:   "bg-yellow-100 text-yellow-700",
  esclusa:   "bg-red-100 text-red-600",
  chiusa:    "bg-blue-100 text-blue-700",
  eliminata: "bg-red-200 text-red-900 line-through",
};

const isRigaEsclusa = (line) => line?.stato === "esclusa" || line?.stato === "eliminata";
const statoLabel = (stato) => (stato === "esclusa" || stato === "eliminata" ? "eliminato" : stato);
const SHOW_ELIMINATI_KEY = "parcellazione.altriAdempimenti.showEliminati";
const INCLUDE_CESSATI_KEY = "parcellazione.altriAdempimenti.includeCessati";
const SHOW_UNIFICATE_KEY = "parcellazione.altriAdempimenti.showUnificate";
const HIDE_ZERO_KEY     = "parcellazione.altriAdempimenti.hideZero";
const SHOW_BOZZE_KEY    = "parcellazione.altriAdempimenti.showBozze";
const MESE_COMPETENZA_FALLBACK_CLIENTI = [
  "CALVI",
  ...(process.env.REACT_APP_MESE_COMPETENZA_AUTOMATICO_CLIENTI || "")
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean),
];
const SETTORI_ALTRI_ADEMPIMENTI = [
  { id: "PAGHE", name: "Paghe", roles: ["parc_paghe", "parc_team_leader"] },
  { id: "ASSUNZIONI", name: "Assunzioni", roles: ["parc_assunzioni"] },
  { id: "CONSULENZA", name: "Consulenza", roles: ["parc_consulente"] },
];
const SETTORE_LABEL = Object.fromEntries(SETTORI_ALTRI_ADEMPIMENTI.map((settore) => [settore.id, settore.name]));
const TIPOLOGIE_FATTURAZIONE_BUTTONS = [
  { t: "Z", active: "bg-blue-100 text-blue-700 border-blue-400 ring-1 ring-blue-300", idle: "bg-white text-gray-500 border-gray-200 hover:bg-gray-50" },
  { t: "I", active: "bg-green-100 text-green-700 border-green-400 ring-1 ring-green-300", idle: "bg-white text-gray-500 border-gray-200 hover:bg-gray-50" },
  { t: "B", active: "bg-purple-100 text-purple-700 border-purple-400 ring-1 ring-purple-300", idle: "bg-white text-gray-500 border-gray-200 hover:bg-gray-50" },
];
const GRID_TOOLBAR_BUTTON_SX = {
  minHeight: 30,
  px: 1.2,
  color: "#1565c0",
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: 0,
  textTransform: "uppercase",
  "&:hover": { backgroundColor: "#eff6ff" },
  "& .MuiButton-startIcon": { mr: 0.75 },
  "& .MuiSvgIcon-root": { fontSize: 18 },
};

// Slot con identità stabile per toolbar e footer della griglia: definire il
// componente dentro il render della pagina ne cambia il tipo a ogni ciclo e
// React rimonta l'intero blocco. Con il filterModel controllato ogni lettera
// digitata nella ricerca fa ri-renderizzare la pagina, quindi il campo veniva
// smontato e perdeva il focus dopo pochi caratteri.
const GridSlotStabile = ({ render }) => render();

const getSettoriAltriAdempimenti = () => {
  if (knowAcces(["parc_admin"])) {
    return SETTORI_ALTRI_ADEMPIMENTI.map(({ id, name }) => ({ id, name }));
  }
  return SETTORI_ALTRI_ADEMPIMENTI
    .filter((settore) => knowAcces(settore.roles))
    .map(({ id, name }) => ({ id, name }));
};

const getDefaultSettoreAltriAdempimenti = () => {
  if (knowAcces(["parc_admin"])) return "CONSULENZA";
  // Con doppio ruolo Assunzioni + Paghe si parte dal perimetro Assunzioni
  // (tutti i clienti); selezionando Paghe la lista viene poi ristretta agli
  // incarichi Payroll attivi dell'utente.
  if (knowAcces(["parc_assunzioni"])) return "ASSUNZIONI";
  return getSettoriAltriAdempimenti()[0]?.id || "";
};

const fmt = (v) =>
  v != null
    ? formatItalianNumber(v, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "—";

const getLineCreatedAtTime = (line) => {
  const time = new Date(line?.created_at || 0).getTime();
  return Number.isFinite(time) ? time : 0;
};

const getLineNumericId = (line) => {
  if (Array.isArray(line?.original_ids) && line.original_ids.length > 0) {
    return Math.max(...line.original_ids.map((id) => Number(id) || 0));
  }
  return Number(line?.id) || 0;
};

const compareLineePiuRecenti = (a, b) => {
  const dateDiff = getLineCreatedAtTime(b) - getLineCreatedAtTime(a);
  if (dateDiff !== 0) return dateDiff;
  return getLineNumericId(b) - getLineNumericId(a);
};

const deveMostrareQuantitaListino = (prestazione) => {
  if (!prestazione || prestazione.voce_particolare) return false;
  if (prestazione.quantita_obbligatoria !== undefined && prestazione.quantita_obbligatoria !== null) {
    return Number(prestazione.quantita_obbligatoria) === 1;
  }
  return ![1, 2, 6, 8].includes(Number(prestazione.metodo_fatturazione));
};

const isClienteMeseCompetenzaAutomatico = (azienda) => {
  if (Number(azienda?.mese_competenza_automatico) === 1) return true;
  const ragioneSociale = String(azienda?.ragione_sociale || "").toUpperCase();
  return MESE_COMPETENZA_FALLBACK_CLIENTI.some((cliente) => ragioneSociale.includes(cliente));
};

const formatMeseCompetenza = (mese, anno) => {
  const data = new Date(Number(anno), Number(mese) - 1, 1);
  const meseLabel = data.toLocaleDateString("it-IT", { month: "long" });
  return `Mese di competenza: ${meseLabel} ${anno}`;
};

const formatPeriodoCedolini = (value) => {
  const match = String(value || "").match(/^(\d{4})-(\d{2})/);
  if (!match) return "—";
  return new Date(Number(match[1]), Number(match[2]) - 1, 1).toLocaleDateString("it-IT", {
    month: "long",
    year: "numeric",
  });
};

const mergeMeseCompetenzaNote = (note, mese, anno) => {
  const meseCompetenza = formatMeseCompetenza(mese, anno);
  const notePulite = String(note || "").trim();
  if (!notePulite) return meseCompetenza;
  if (notePulite.toLowerCase().includes(meseCompetenza.toLowerCase())) return notePulite;
  return `${notePulite}; ${meseCompetenza}`;
};

const joinDescrizioni = (values) => {
  const result = [];
  values.forEach((value) => {
    String(value || "")
      .split(";")
      .map((v) => v.trim())
      .filter(Boolean)
      .forEach((v) => {
        if (!result.includes(v)) result.push(v);
      });
  });
  return result.join("; ") || null;
};

const joinDescrizioniFattura = (values) => {
  const result = [];
  values.forEach((value) => {
    String(value || "")
      .split(";")
      .map((item) => item.trim())
      .filter(Boolean)
      .forEach((item) => result.push(item));
  });
  return result.join("; ") || null;
};

const appendCodiceClienteIipay = (descrizione, line) => {
  const codiceCliente = line?.cliente_diretto_iipay && line?.codice_cliente_iipay
    ? String(line.codice_cliente_iipay).trim()
    : "";
  const descrizionePulita = String(descrizione || "").trim();

  if (!codiceCliente) return descrizionePulita;
  if (!descrizionePulita) return codiceCliente;
  if (descrizionePulita.toUpperCase().includes(codiceCliente.toUpperCase())) return descrizionePulita;
  return `${descrizionePulita} ${codiceCliente}`;
};

const composeDescrizioneFattura = (line) => {
  const descrizionePrestazione = String(line?.descrizione_prestazione || "").trim();
  const descrizioneExtra = String(line?.descrizione_extra_fattura || "").trim();
  if (line?.sezione === "altri_adempimenti") {
    return appendCodiceClienteIipay(descrizioneExtra, line);
  }
  const appendVoce = descrizionePrestazione &&
    String(line?.descrizione_fattura || "").startsWith(descrizionePrestazione);
  const descrizione = descrizioneExtra && appendVoce
    ? `${descrizionePrestazione} - ${descrizioneExtra}`
    : descrizioneExtra
    ? descrizioneExtra
    : descrizionePrestazione;

  return appendCodiceClienteIipay(descrizione, line);
};

const CODICI_NON_UNIFICABILI = new Set(["1222", "1236"]);

const isRigaUnificabile = (line) =>
  line?.sezione === "altri_adempimenti" &&
  !CODICI_NON_UNIFICABILI.has(
    String(line?.nuovo_codice_ago || line?.prestazione || "").trim().replace(/^0+(?=\d)/, "")
  ) &&
  line?.prestazione != null &&
  line?.quantita != null &&
  line?.importo_unitario != null &&
  !["esclusa", "eliminata", "forzata"].includes(line?.stato);

const groupAltriAdempimentiLines = (lines = []) => {
  const grouped = new Map();
  const output = [];

  lines.forEach((line) => {
    if (!isRigaUnificabile(line)) {
      output.push(line);
      return;
    }

    const key = [
      line.billing_period_id,
      line.codice_univoco_azienda,
      line.prestazione,
      line.importo_unitario,
      line.stato,
    ].join("|");

    if (!grouped.has(key)) {
      const clone = { ...line, id: `unified-${key}`, original_ids: [line.id], is_unified: false };
      grouped.set(key, clone);
      output.push(clone);
      return;
    }

    const current = grouped.get(key);
    current.is_unified = true;
    current.original_ids.push(line.id);
    if (compareLineePiuRecenti(line, current) < 0) {
      current.created_at = line.created_at;
    }
    current.quantita = Number(
      ((Number(current.quantita) || 0) + (Number(line.quantita) || 0)).toFixed(2)
    );
    current.totale = Number(((Number(current.totale) || 0) + (Number(line.totale) || 0)).toFixed(2));
    current.descrizione_extra_fattura = joinDescrizioniFattura([current.descrizione_extra_fattura, line.descrizione_extra_fattura]);
    current.descrizione_interna = joinDescrizioni([current.descrizione_interna, line.descrizione_interna]);
    current.note_admin = joinDescrizioni([current.note_admin, line.note_admin]);
    current.descrizione_fattura = composeDescrizioneFattura(current);
    current.created_by = joinDescrizioni([current.created_by, line.created_by]);
  });

  return output;
};

const METODO_FATTURAZIONE_BADGE_COLORS = {
  libera: "bg-amber-100 text-amber-800",
  libero: "bg-amber-100 text-amber-800",
  "listino/forfait": "bg-teal-100 text-teal-700",
  "a tabella": "bg-indigo-100 text-indigo-700",
  "a fascia": "border border-blue-200 bg-blue-50 text-blue-700",
  orario: "bg-cyan-100 text-cyan-700",
  unitaria: "bg-lime-100 text-lime-700",
};

const formatPrestazioneListinoName = (prestazione) => {
  const codice = parseInt(prestazione.nuovo_codice_ago || prestazione.codice, 10);
  return `${Number.isNaN(codice) ? prestazione.codice : codice} – ${prestazione.descrizione}`;
};

const normalizeCodiceListino = (codice) => {
  const raw = String(codice || "").trim();
  if (!raw) return "";
  const normalized = raw.replace(/^0+(?=\d)/, "");
  return normalized || "0";
};

const getCodicePrestazioneSort = (prestazione) => {
  const codice = normalizeCodiceListino(prestazione?.nuovo_codice_ago || prestazione?.codice);
  const numeric = Number(codice);
  return Number.isFinite(numeric) ? numeric : codice;
};

const isVoceScontoListino = (prestazione) =>
  /\bscont/i.test(`${prestazione?.descrizione || ""} ${prestazione?.note || ""}`);

const getImportoListinoDefault = (prestazione) => {
  if (!prestazione || prestazione.importo == null || prestazione.importo === "") return 0;
  const importo = Number(prestazione.importo);
  if (!Number.isFinite(importo)) return 0;
  return isVoceScontoListino(prestazione) && importo > 0 ? -importo : importo;
};

const normalizeImportoSconto = (prestazione, value) => {
  if (value === "" || value == null) return value;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return value;
  return isVoceScontoListino(prestazione) ? -Math.abs(numeric) : numeric;
};

const isPrestazioneZeroListino = (prestazione) => {
  if (Number(prestazione?.a_zero) === 1) return true;
  const metodoFatturazione = getMetodoFatturazioneListino(prestazione);
  const hasImporto = prestazione.importo != null && Number(getImportoListinoDefault(prestazione)) !== 0;
  return !hasImporto && metodoFatturazione === "listino";
};

const isPrestazioneAZero = (prestazione) => Number(prestazione?.a_zero) === 1;

const isImportoDiversoDaListino = (prestazione, importoUnitario) => {
  if (!prestazione || importoUnitario === "" || importoUnitario == null) return false;
  // Libera e Tabella non hanno un prezzo fisso da rispettare: l'importo
  // inserito dall'operatore non va confrontato con il valore del master.
  if ([1, 2, 6, 8].includes(Number(prestazione.metodo_fatturazione))) return false;
  const importoListino = getImportoListinoDefault(prestazione);
  const importoInserito = Number(importoUnitario);
  if (!Number.isFinite(importoInserito)) return false;
  return Math.abs(importoInserito - importoListino) > 0.005;
};

const isTotaleDiversoDaListino = (prestazione, totale) => {
  if (!prestazione || totale === "" || totale == null) return false;
  if (![3, 4].includes(Number(prestazione.metodo_fatturazione))) return false;
  if (prestazione.importo == null || prestazione.importo === "") return false;
  const totaleListino = getImportoListinoDefault(prestazione);
  const totaleInserito = Number(totale);
  if (!Number.isFinite(totaleInserito)) return false;
  return Math.abs(totaleInserito - totaleListino) > 0.005;
};

const isImportoZeroVoceATabella = (prestazione, importoUnitario) => {
  if (Number(prestazione?.metodo_fatturazione) !== 6 || isIipayVoceAFascia(prestazione)) return false;
  if (importoUnitario === "" || importoUnitario == null) return false;
  const importo = Number(importoUnitario);
  return Number.isFinite(importo) && Math.abs(importo) < 0.005;
};

const buildPrestazioneFromLine = (line) => {
  if (!line?.prestazione) return null;
  return {
    codice: line.prestazione,
    descrizione: line.descrizione_prestazione || line.descrizione_extra_fattura || `Prestazione ${line.prestazione}`,
    voce_particolare: line.voce_particolare,
    settore_listino_id: line.settore_prestazione_id,
    settore_listino: line.settore,
    importo: line.importo_default_prestazione ?? line.importo_unitario ?? null,
    metodo_fatturazione: line.metodo_fatturazione_prestazione,
    richiesta: line.richiesta,
    quantita_obbligatoria: line.quantita_obbligatoria,
    nuovo_codice_ago: line.nuovo_codice_ago,
    a_zero: line.a_zero,
    iipay_ha_fascia: line.iipay_ha_fascia,
    iipay_min_fascia_applicata: line.iipay_min_fascia_applicata,
    iipay_max_fascia_applicata: line.iipay_max_fascia_applicata,
  };
};

const getPrestazioneListinoBadges = (prestazione, settoreSel) => {
  const metodoFatturazione = getMetodoFatturazioneListino(prestazione);
  const importoDefault = getImportoListinoDefault(prestazione);
  const hasImporto = prestazione.importo != null && Number(importoDefault) !== 0;
  const showZero = isPrestazioneZeroListino(prestazione);
  const badges = [];

  if (!prestazione.voce_particolare && (hasImporto || showZero)) {
    badges.push({
      label: hasImporto
        ? `${formatItalianNumber(importoDefault, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
        : "zero",
      className: hasImporto ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-700",
    });
  }

  if (metodoFatturazione && metodoFatturazione !== "listino" && (!showZero || metodoFatturazione === "a fascia")) {
    badges.push({
      label: metodoFatturazione === "a fascia" ? `A fascia${getIipayFasciaLabel(prestazione) ? ` ${getIipayFasciaLabel(prestazione)}` : ""}` : metodoFatturazione,
      className: METODO_FATTURAZIONE_BADGE_COLORS[metodoFatturazione],
    });
  }

  if (prestazione.voce_particolare && prestazione.note) {
    badges.push({
      label: prestazione.note,
      className: "bg-rose-100 text-rose-700",
    });
  }

  const settoreListino = String(prestazione.settore_listino || "").trim();
  const isSettoreTecnico = ["ADMIN", "SOLO ADMIN", "TUTTI"].includes(settoreListino.toUpperCase());
  if (settoreListino && settoreListino !== settoreSel && !isSettoreTecnico) {
    badges.push({
      label: settoreListino,
      className: "bg-slate-100 text-slate-700",
    });
  }

  return badges;
};

const getIipayFasciaApplicataLabel = (prestazione) => {
  if (Number(prestazione?.iipay_rate_card) !== 1) return "";
  if (Number(prestazione?.iipay_manca_cedolini_precedenti) === 1) return "";

  const min = prestazione?.iipay_min_fascia_applicata;
  const max = prestazione?.iipay_max_fascia_applicata;

  if (min == null && max == null) return "senza fascia";
  if (min != null && max != null) return `${min}-${max}`;
  if (min != null) return `da ${min}`;
  return `fino a ${max}`;
};

export const AltriAdempimenti = ({ data, aziende, onPeriodChange, tuttiTeam = false, onTuttiTeamChange, showTuttiTeam = false, livePeriodo, onRealtimeChange }) => {
  const [periodo, setPeriodo]       = useState(null);
  const periodRequestRef = useRef(0);
  const [linee, setLinee]           = useState([]);
  const [loading, setLoading]       = useState(false);
  const [insertOpen, setInsertOpen]     = useState(false);
  const [insertTarget, setInsertTarget] = useState(null); // { periodo, anno, mese }
  const [editLine, setEditLine]         = useState(null); // riga da modificare
  const [excludeOpen, setExcludeOpen]   = useState(false);
  const [rejectOpen, setRejectOpen]     = useState(false);
  const [selectedLine, setSelectedLine] = useState(null);
  const [motivo, setMotivo]             = useState("");
  const [showEliminati, setShowEliminati] = useState(() => localStorage.getItem(SHOW_ELIMINATI_KEY) === "true");
  const [showUnificate, setShowUnificate] = useState(() => localStorage.getItem(SHOW_UNIFICATE_KEY) === "true");
  const [hideZero, setHideZero]           = useState(() => localStorage.getItem(HIDE_ZERO_KEY) === "true");
  const [showBozze, setShowBozze]         = useState(() => localStorage.getItem(SHOW_BOZZE_KEY) === "true");
  const [totaliOpen, setTotaliOpen]       = useState(false);
  const [tipologieTotali, setTipologieTotali] = useState(new Set());
  const [cessatiCodici, setCessatiCodici] = useState(new Set());
  const [filterCessati, setFilterCessati] = useState(false);
  const [tipologieFiltroRighe, setTipologieFiltroRighe] = useState(new Set());
  const [gridFilterModel, setGridFilterModel] = useState({ items: [], quickFilterValues: [] });
  const [aziendeConsentite, setAziendeConsentite] = useState(aziende || []);
  const [aziendePagheConsentite, setAziendePagheConsentite] = useState([]);

  const isAdmin = knowAcces(["parc_admin"]);
  const isAmministrazione = knowAcces(["parc_amministrazione"]);
  const [anno, mese] = data ? data.split("-").map(Number) : [null, null];

  useEffect(() => {
    const targetAnno = insertTarget?.anno || anno;
    const targetMese = insertTarget?.mese || mese;
    if (!targetAnno || !targetMese) return;
    const controller = new AbortController();
    const commonParams = {
      data: `${targetAnno}-${String(targetMese).padStart(2, "0")}`,
      scope: "altri_adempimenti",
      tutti: tuttiTeam ? "1" : "0",
    };
    Promise.all([
      api.get("/aziende/parcellazione", { params: commonParams, signal: controller.signal }),
      api.get("/aziende/parcellazione", { params: { ...commonParams, settore: "PAGHE" }, signal: controller.signal }),
    ]).then(([resTutti, resPaghe]) => {
      const payload = resTutti.data || {};
      const payloadPaghe = resPaghe.data || {};
      setAziendeConsentite([...(payload.zucch || []), ...(payload.bpoint || []), ...(payload.iipay || [])]);
      setAziendePagheConsentite([...(payloadPaghe.zucch || []), ...(payloadPaghe.bpoint || []), ...(payloadPaghe.iipay || [])]);
    }).catch((error) => {
      if (error?.code !== "ERR_CANCELED") toast.error("Errore caricamento clienti autorizzati");
    });
    return () => controller.abort();
  }, [anno, mese, insertTarget?.anno, insertTarget?.mese, tuttiTeam]);

  // carica cessati una volta sola per filtro righe (operatori) e filtro cessati (admin)
  useEffect(() => {
    api.get("/aziende/parcellazione/cessati", { params: { tutti: tuttiTeam ? "1" : "0" } })
      .then((res) => setCessatiCodici(new Set((res.data || []).map((a) => a.codice_univoco))))
      .catch(() => {});
  }, [tuttiTeam]);

  const loadPeriodo = useCallback(async () => {
    if (!anno || !mese) return;
    const request = ++periodRequestRef.current;
    try {
      const res = await api.get(`/billing/periods/${anno}/${mese}`);
      if (request === periodRequestRef.current) setPeriodo(res.data);
    } catch {
      toast.error("Errore caricamento periodo");
    }
  }, [anno, mese]);

  const loadLinee = useCallback(async (periodoId) => {
    if (!periodoId) return;
    setLoading(true);
    try {
      const res = await api.get(`/billing/lines/${periodoId}`, {
        params: { sezione: "altri_adempimenti", tutti: tuttiTeam ? "1" : "0" },
      });
      setLinee(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error("Errore caricamento adempimenti");
    } finally {
      setLoading(false);
    }
  }, [tuttiTeam]);

  useEffect(() => {
    if (Number(livePeriodo?.anno) !== anno || Number(livePeriodo?.mese) !== mese) return;
    periodRequestRef.current += 1;
    setPeriodo(livePeriodo);
  }, [livePeriodo, anno, mese]);

  const onAdempimentiChange = (event) => {
    if (event?.period) {
      periodRequestRef.current += 1;
      setPeriodo((current) => ({ ...current, ...event.period }));
    } else if (event?.action === "connected") loadPeriodo();
    else if (periodo?.id) loadLinee(periodo.id);
    if (event?.period || event?.action === "connected") onRealtimeChange?.();
  };
  useAdempimentiRealtime({ anno, mese, onChange: onAdempimentiChange });
  useAdempimentiRealtime({
    anno: insertTarget?.anno, mese: insertTarget?.mese,
    enabled: Boolean(insertOpen && insertTarget && (insertTarget.anno !== anno || insertTarget.mese !== mese)),
    onChange: async (event) => {
      let updated = event?.period;
      if (!updated) {
        try { updated = (await api.get(`/billing/periods/${insertTarget.anno}/${insertTarget.mese}`)).data; }
        catch { return; }
      }
      setInsertTarget((current) => ({ ...current, periodo: { ...current.periodo, ...updated } }));

    },
  });

  useEffect(() => { loadPeriodo(); }, [loadPeriodo]);
  useEffect(() => {
    if (periodo?.id) loadLinee(periodo.id);
    else setLinee([]);
  }, [periodo, loadLinee]);
  useEffect(() => {
    localStorage.setItem(SHOW_ELIMINATI_KEY, String(showEliminati));
  }, [showEliminati]);
  useEffect(() => {
    localStorage.setItem(SHOW_UNIFICATE_KEY, String(showUnificate));
  }, [showUnificate]);
  useEffect(() => {
    localStorage.setItem(HIDE_ZERO_KEY, String(hideZero));
  }, [hideZero]);
  useEffect(() => {
    localStorage.setItem(SHOW_BOZZE_KEY, String(showBozze));
  }, [showBozze]);

  const getRealId = (row) => row.original_ids?.[0] ?? row.id;

  const handleValida = async (id) => {
    const nuovoStato = isAdmin ? "forzata" : "validata";
    try {
      await api.put(`/billing/lines/${id}`, { stato: nuovoStato });
      toast.success(isAdmin ? "Adempimento forzato" : "Adempimento validato");
      loadLinee(periodo.id);
    } catch {
      toast.error(isAdmin ? "Errore forzatura" : "Errore validazione");
    }
  };

  const handleEscludi = async () => {
    try {
      await api.post(`/billing/lines/${getRealId(selectedLine)}/exclude`, { motivo });
      toast.success("Adempimento eliminato");
      setExcludeOpen(false);
      loadLinee(periodo.id);
    } catch {
      toast.error("Errore eliminazione");
    }
  };

  const handleElimina = async (line) => {
    try {
      await api.put(`/billing/lines/${getRealId(line)}`, { stato: "esclusa" });
      toast.success("Adempimento rimosso");
      loadLinee(periodo.id);
    } catch {
      toast.error("Errore eliminazione");
    }
  };

  const handleApprova = async (line) => {
    try {
      await api.post(`/billing/lines/${getRealId(line)}/propose/approve`);
      toast.success("Proposta approvata");
      loadLinee(periodo.id);
    } catch {
      toast.error("Errore approvazione");
    }
  };

  const handleRifiuta = async () => {
    try {
      await api.post(`/billing/lines/${getRealId(selectedLine)}/propose/reject`, { motivo });
      toast.success("Proposta rifiutata");
      setRejectOpen(false);
      loadLinee(periodo.id);
    } catch {
      toast.error("Errore rifiuto");
    }
  };

  const lineeSicure = (Array.isArray(linee) ? linee : []).filter(line => String(line.tipologia_fatturazione || '').trim().toUpperCase() !== 'G');
  const lineeVisibili = isAdmin
    ? (showEliminati ? lineeSicure : lineeSicure.filter((l) => !isRigaEsclusa(l)))
    : lineeSicure.filter((l) => !isRigaEsclusa(l));
  const lineeVisibiliByTipologia = tipologieFiltroRighe.size > 0
    ? lineeVisibili.filter((l) => tipologieFiltroRighe.has(String(l.tipologia_fatturazione || "").toUpperCase()))
    : lineeVisibili;
  const lineeVisibiliByStato = showBozze
    ? lineeVisibiliByTipologia.filter((l) => ["bozza", "forzata"].includes(l.stato))
    : lineeVisibiliByTipologia;
  const lineeVisibiliFiltered = hideZero
    ? lineeVisibiliByStato.filter((l) => Number(l.totale) !== 0 && l.totale != null)
    : lineeVisibiliByStato;
  const lineeVisibiliFilteredCessati = isAdmin && filterCessati
    ? lineeVisibiliFiltered.filter((l) => cessatiCodici.has(l.codice_univoco_azienda))
    : lineeVisibiliFiltered;
  const lineeOrdinate = [...lineeVisibiliFilteredCessati].sort(compareLineePiuRecenti);
  const lineeFiltrate = showUnificate
    ? groupAltriAdempimentiLines(lineeOrdinate).sort(compareLineePiuRecenti)
    : lineeOrdinate;

  const totaliPerVoce = useMemo(() => {
    const result = new Map();
    lineeFiltrate.filter((linea) => tipologieTotali.size === 0 || tipologieTotali.has(String(linea.tipologia_fatturazione || "").toUpperCase()))
      .forEach((linea) => {
        const codice = String(linea.nuovo_codice_ago || linea.prestazione || "—").replace(/^0+(?=\d)/, "");
        const current = result.get(codice) || {
          codice,
          descrizione: linea.descrizione_prestazione || linea.descrizione_fattura || "Prestazione",
          quantita: 0,
          totale: 0,
          tipologie: new Set(),
        };
        current.quantita += Number(linea.quantita || 0);
        current.totale += Number(linea.totale || 0);
        current.tipologie.add(String(linea.tipologia_fatturazione || "").toUpperCase());
        result.set(codice, current);
      });
    return [...result.values()].sort((a, b) => a.codice.localeCompare(b.codice, "it", { numeric: true }));
  }, [lineeFiltrate, tipologieTotali]);
  const totaleVoci = useMemo(() => totaliPerVoce.reduce((sum, voce) => sum + voce.totale, 0), [totaliPerVoce]);

  const totaleSezione = lineeSicure
    .filter((l) => !isRigaEsclusa(l))
    .reduce((s, l) => s + (Number(l.totale) || 0), 0);
  const totaliSezionePerTipologia = linee
    .filter((linea) => !isRigaEsclusa(linea))
    .reduce((result, linea) => {
      const tipo = String(linea.tipologia_fatturazione || "Z").toUpperCase();
      result[tipo] = (result[tipo] || 0) + Number(linea.totale || 0);
      return result;
    }, { Z: 0, B: 0, I: 0 });

  const hasGridFilters = Boolean(
    gridFilterModel.items?.length ||
    gridFilterModel.quickFilterValues?.filter(Boolean).length
  );
  const handleClearGridFilters = () => {
    setGridFilterModel({ items: [], quickFilterValues: [] });
  };

  const columns = [
    {
      field: "tipologia_fatturazione",
      headerName: "Tipo",
      width: 55,
      headerAlign: "center",
      align: "center",
      renderCell: (p) => {
        const colors = { Z: "bg-blue-100 text-blue-700", B: "bg-purple-100 text-purple-700", I: "bg-green-100 text-green-700" };
        return p.value ? (
          <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${colors[p.value] || "bg-gray-100 text-gray-600"}`}>{p.value}</span>
        ) : null;
      },
    },
    {
      field: "ragione_sociale",
      headerName: "Cliente",
      flex: 2,
      minWidth: 160,
      renderCell: (params) => (
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="truncate">{params.value}</span>
          {params.row.data_fine_elaborazioni && (
            <span className="shrink-0 px-1.5 py-0.5 rounded text-xs font-semibold bg-slate-100 text-slate-700">
              cessato
            </span>
          )}
        </div>
      ),
    },
    {
      field: "prestazione",
      headerName: "Cod.",
      width: 65,
      headerAlign: "center",
      align: "center",
      valueGetter: (params) =>
        params.row.nuovo_codice_ago
          ? parseInt(params.row.nuovo_codice_ago, 10)
          : params.row.prestazione,
    },
    {
      field: "descrizione_prestazione",
      headerName: "Prestazione",
      flex: 1,
      minWidth: 130,
    },
    ...(isAdmin ? [{
      field: "note_admin",
      headerName: "Note per Diego",
      flex: 1,
      minWidth: 180,
    }] : []),
    {
      field: "descrizione_fattura",
      headerName: "Descrizione fattura",
      flex: 2,
      minWidth: 180,
      valueGetter: (params) => params.row.descrizione_fattura || params.row.descrizione_extra_fattura,
    },
    {
      field: "totale",
      headerName: "Totale €",
      width: 100,
      headerAlign: "right",
      align: "right",
      valueFormatter: (p) => fmt(p.value),
    },
    {
      field: "quantita",
      headerName: "Qtà",
      width: 75,
      headerAlign: "right",
      align: "right",
      valueFormatter: (p) => p.value != null
        ? formatItalianNumber(p.value, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : "—",
    },
    {
      field: "importo_unitario",
      headerName: "Unit. €",
      width: 90,
      headerAlign: "right",
      align: "right",
      valueFormatter: (p) => fmt(p.value),
    },
    {
      field: "created_by_nome",
      headerName: "Inserito da",
      width: 130,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "settore",
      headerName: "Settore",
      width: 115,
      headerAlign: "center",
      align: "center",
      valueGetter: (params) => SETTORE_LABEL[String(params.value || "").toUpperCase()] || params.value || "—",
    },
    {
      field: "stato",
      headerName: "Stato",
      width: 120,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <div className="flex items-center gap-1">
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATO_COLORS[params.value] || ""}`}>
            {statoLabel(params.value)}
          </span>
          {params.row.proposta_totale != null && (
            <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
              proposta
            </span>
          )}
          {params.row.is_unified && (
            <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
              unificata
            </span>
          )}
        </div>
      ),
    },
    {
      field: "azioni",
      headerName: "Azioni",
      width: 150,
      sortable: false,
      filterable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        if (params.row.is_unified) return null;
        const { stato, proposta_totale } = params.row;
        const rigaBloccata = adempimentiClosed(periodo, params.row.tipologia_fatturazione || params.row.tipo, isAdmin);
        const hasProposta = proposta_totale != null;
        const isEliminata = isRigaEsclusa(params.row);
        const canUpdate = Boolean(params.row.can_update);
        return (
          <div className="flex gap-1">
            {/* Modifica — non-admin su righe non chiuse/eliminate senza proposta pendente */}
            {!rigaBloccata && !isAdmin && canUpdate && !hasProposta && stato !== "chiusa" && !isEliminata && (
              <Button
                className="edit-button"
                type="button"
                icon={<MdEdit className="button-icon" />}
                onClick={() => {
                  setEditLine(params.row);
                  setInsertTarget({ periodo, anno, mese });
                  setInsertOpen(true);
                }}
              />
            )}
            {/* Non-admin: elimina propria riga non chiusa/eliminata */}
            {!rigaBloccata && !isAdmin && canUpdate && stato !== "chiusa" && !isEliminata && (
              <Button
                className="delete-button"
                type="button"
                icon={<MdClose className="button-icon" />}
                onClick={() => handleElimina(params.row)}
              />
            )}
            {/* Admin: approva/rifiuta proposta pendente */}
            {!rigaBloccata && isAdmin && hasProposta && (
              <>
                <Button
                  className="success-button"
                  type="button"
                  icon={<MdThumbUp className="button-icon" />}
                  onClick={() => handleApprova(params.row)}
                />
                <Button
                  className="delete-button"
                  type="button"
                  icon={<MdThumbDown className="button-icon" />}
                  onClick={() => {
                    setSelectedLine(params.row);
                    setMotivo("");
                    setRejectOpen(true);
                  }}
                />
              </>
            )}
            {/* Admin: modifica (apre modale) e escludi */}
            {!rigaBloccata && isAdmin && !hasProposta && !isEliminata && stato !== "chiusa" && (
              <>
                {stato === "bozza" && (
                  <Button
                    className="success-button"
                    type="button"
                    icon={<MdCheck className="button-icon" />}
                    onClick={() => handleValida(getRealId(params.row))}
                  />
                )}
                <Button
                  className="edit-button"
                  type="button"
                  icon={<MdEdit className="button-icon" />}
                  onClick={() => {
                    setEditLine(params.row);
                    setInsertTarget({ periodo, anno, mese });
                    setInsertOpen(true);
                  }}
                />
                {stato !== "esclusa" && (
                  <Button
                    className="delete-button"
                    type="button"
                    icon={<MdClose className="button-icon" />}
                    onClick={() => {
                      setSelectedLine(params.row);
                      setMotivo("");
                      setExcludeOpen(true);
                    }}
                  />
                )}
              </>
            )}
          </div>
        );
      },
    },
  ];

  const handleAggiungi = async () => {
    if (!periodo) return;
    setEditLine(null);
    setInsertTarget({ periodo, anno, mese });
    setInsertOpen(true);
  };

  const handleExportExcel = () => {
    if (!lineeFiltrate.length) {
      toast.warning("Nessuna riga da esportare");
      return;
    }

    const rows = lineeFiltrate.map((l) => ({
      Tipo: l.tipologia_fatturazione || "",
      Cliente: l.ragione_sociale || "",
      "Cod. parcellazione": String(l.codice_parcellazione || "").replace(/^0+(?=\d)/, ""),
      "Inserito da": l.created_by_nome || "",
      Cessato: l.data_fine_elaborazioni ? "Sì" : "",
      "Cod. prestazione": String(l.nuovo_codice_ago || l.prestazione || "").replace(/^0+(?=\d)/, ""),
      Prestazione: l.descrizione_prestazione || "",
      "Descrizione fattura": l.descrizione_fattura || l.descrizione_extra_fattura || "",
      "Descrizione interna": l.descrizione_interna || "",
      Quantità: l.quantita ?? "",
      "Importo unitario": l.importo_unitario != null ? Number(l.importo_unitario) : "",
      Totale: l.totale != null ? Number(l.totale) : "",
      Stato: statoLabel(l.stato) || "",
      Settore: l.settore || "",
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [
      { wch: 8 },
      { wch: 36 },
      { wch: 20 },
      { wch: 22 },
      { wch: 10 },
      { wch: 16 },
      { wch: 32 },
      { wch: 42 },
      { wch: 42 },
      { wch: 10 },
      { wch: 15 },
      { wch: 12 },
      { wch: 12 },
      { wch: 14 },
    ];
    if (ws["!ref"]) {
      ws["!autofilter"] = { ref: ws["!ref"] };
      ws["!freeze"] = { xSplit: 0, ySplit: 1, topLeftCell: "A2", activePane: "bottomLeft", state: "frozen" };

      const range = XLSX.utils.decode_range(ws["!ref"]);
      const border = { style: "thin", color: { rgb: "D9D9D9" } };
      const borders = { top: border, bottom: border, left: border, right: border };
      const headerStyle = {
        fill: { patternType: "solid", fgColor: { rgb: "1D4ED8" } },
        font: { bold: true, color: { rgb: "FFFFFF" } },
        alignment: { horizontal: "center", vertical: "center", wrapText: true },
        border: borders,
      };
      const bodyStyle = { alignment: { vertical: "center" }, border: borders };

      for (let rowIndex = range.s.r; rowIndex <= range.e.r; rowIndex += 1) {
        for (let colIndex = range.s.c; colIndex <= range.e.c; colIndex += 1) {
          const address = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
          if (!ws[address]) ws[address] = { t: "s", v: "" };
          ws[address].s = rowIndex === 0 ? headerStyle : bodyStyle;
        }
      }

      const headers = Object.keys(rows[0]);
      ["Importo unitario", "Totale"].forEach((header) => {
        const colIndex = headers.indexOf(header);
        if (colIndex < 0) return;
        for (let rowIndex = 1; rowIndex <= range.e.r; rowIndex += 1) {
          const cell = ws[XLSX.utils.encode_cell({ r: rowIndex, c: colIndex })];
          if (cell && cell.v !== "") cell.z = '#,##0.00 [$€-it-IT]';
        }
      });
      const quantitaColIndex = headers.indexOf("Quantità");
      if (quantitaColIndex >= 0) {
        for (let rowIndex = 1; rowIndex <= range.e.r; rowIndex += 1) {
          const cell = ws[XLSX.utils.encode_cell({ r: rowIndex, c: quantitaColIndex })];
          if (cell && cell.v !== "") cell.z = "#,##0.00";
        }
      }
      ws["!rows"] = [{ hpt: 24 }];
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Altri adempimenti");
    XLSX.writeFile(wb, `altri_adempimenti_${anno}_${String(mese).padStart(2, "0")}.xlsx`);
  };

  const renderToolbar = () => (
    <GridToolbarContainer>
      <div className="w-full flex flex-wrap-reverse md:flex-nowrap justify-between md:space-x-5 mb-2 gap-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <GridToolbarQuickFilter placeholder="Cerca..." />
          <div className="flex items-center gap-1">
            {TIPOLOGIE_FATTURAZIONE_BUTTONS.map(({ t, active, idle }) => (
              <button
                key={t}
                type="button"
                onClick={() => setTipologieFiltroRighe((prev) => { const next = new Set(prev); next.has(t) ? next.delete(t) : next.add(t); return next; })}
                className={`px-2.5 py-0.5 rounded text-xs font-semibold border transition-colors ${tipologieFiltroRighe.has(t) ? active : idle}`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {showTuttiTeam && (
            <button
              type="button"
              onClick={() => onTuttiTeamChange?.(!tuttiTeam)}
              className={`text-xs rounded px-2 py-1 font-semibold border transition-colors ${tuttiTeam ? "bg-blue-600 text-white border-blue-600" : "bg-white text-blue-700 border-blue-300"}`}
            >
              TUTTI
            </button>
          )}
          <BillingClosureBadges state={billingClosureState(periodo, "altri_adempimenti")} />
          {isAdmin && (
            <>
              <button
                onClick={() => setFilterCessati(v => !v)}
                className={`text-xs rounded px-2 py-1 font-medium border transition-colors ${filterCessati ? "bg-slate-200 text-slate-700 border-slate-400" : "bg-gray-100 text-gray-500 border-gray-300"}`}
              >
                {filterCessati ? "Tutti" : "Solo cessati"}
              </button>
              <button
                onClick={() => setShowEliminati(v => !v)}
                className={`text-xs rounded px-2 py-1 font-medium border transition-colors ${showEliminati ? "bg-red-100 text-red-700 border-red-300" : "bg-gray-100 text-gray-500 border-gray-300"}`}
              >
                {showEliminati ? "Nascondi eliminati" : "Mostra eliminati"}
              </button>
            </>
          )}
          <button
            onClick={() => setShowBozze(v => !v)}
            className={`text-xs rounded px-2 py-1 font-medium border transition-colors ${showBozze ? "bg-amber-100 text-amber-700 border-amber-300" : "bg-gray-100 text-gray-500 border-gray-300"}`}
          >
            {showBozze ? "Mostra tutti" : "Bozze e forzati"}
          </button>
          <button
            onClick={() => setHideZero(v => !v)}
            className={`text-xs rounded px-2 py-1 font-medium border transition-colors ${hideZero ? "bg-orange-100 text-orange-700 border-orange-300" : "bg-gray-100 text-gray-500 border-gray-300"}`}
          >
            {hideZero ? "Mostra zero" : "Nascondi zero"}
          </button>
          <button
            onClick={() => setShowUnificate(v => !v)}
            className={`text-xs rounded px-2 py-1 font-medium border transition-colors ${showUnificate ? "bg-blue-100 text-blue-700 border-blue-300" : "bg-gray-100 text-gray-500 border-gray-300"}`}
          >
            {showUnificate ? "Vista unificata" : "Vista singola"}
          </button>
          {!isAmministrazione && <Button
            className="principal-button"
            text="Aggiungi"
            type="button"
            icon={<HiPlus className="button-icon" />}
            onClick={handleAggiungi}
            disabled={!periodo}
          />}
          <Button
            className={EXPORT_BUTTON_CLASS}
            text="Excel"
            type="button"
            icon={<HiMiniArrowDownTray className="button-icon" />}
            onClick={handleExportExcel}
            disabled={!lineeFiltrate.length}
          />
        </div>
      </div>
    </GridToolbarContainer>
  );

  const renderFooter = () => (
    <div className="flex items-center justify-between px-4 py-2 border-t border-gray-200 bg-gray-50">
      <div className="flex items-center gap-3">
        <GridVisibleRowsCount className="text-sm text-gray-500" />
        <div className="flex items-center rounded border border-gray-100 bg-white shadow-sm">
          <GridToolbarColumnsButton sx={GRID_TOOLBAR_BUTTON_SX} />
          <GridToolbarFilterButton componentsProps={{ button: { sx: GRID_TOOLBAR_BUTTON_SX } }} />
        </div>
        {hasGridFilters && (
          <button
            type="button"
            onClick={handleClearGridFilters}
            className="text-xs font-semibold text-blue-700 hover:text-blue-800"
          >
            Rimuovi filtri
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="w-full h-[calc(100vh-140px)] min-h-[620px] relative el-container flex flex-col gap-3">
      {isAdmin && (
        <TotalSectionCard
          total={totaleSezione}
          totalsByType={totaliSezionePerTipologia}
          onClick={() => setTotaliOpen(true)}
          className="mx-3 mt-3"
        />
      )}
      {insertOpen && insertTarget && (
        <ModalInserisciAdempimento
          periodo={insertTarget.anno === anno && insertTarget.mese === mese ? periodo : insertTarget.periodo}
          aziende={aziendeConsentite}
          aziendePaghe={aziendePagheConsentite}
          anno={insertTarget.anno}
          mese={insertTarget.mese}
          lineaEsistente={editLine}
          tuttiTeam={tuttiTeam}
          onClose={() => { setInsertOpen(false); setEditLine(null); }}
          onSaved={(savedTarget = null) => {
            setInsertOpen(false);
            setEditLine(null);
            const targetAnno = savedTarget?.anno || insertTarget.anno;
            const targetMese = savedTarget?.mese || insertTarget.mese;
            const targetData = `${targetAnno}-${String(targetMese).padStart(2, "0")}`;
            if (targetData !== data && onPeriodChange) {
              onPeriodChange(targetData);
            } else {
              loadLinee(periodo.id);
            }
          }}
        />
      )}

      <Modal
        isOpen={excludeOpen}
        closeButton={() => setExcludeOpen(false)}
        headerText="Elimina adempimento"
        disableOverlayClose
        leftButtons={
          <Button
            className="delete-button"
            text="Elimina"
            type="button"
            icon={<HiOutlineCheck className="button-icon" />}
            onClick={handleEscludi}
          />
        }
      >
        <div className="p-2">
          <p className="text-sm text-gray-500 mb-2">
            {selectedLine?.ragione_sociale} — {selectedLine?.descrizione_extra_fattura || selectedLine?.descrizione_prestazione}
          </p>
          <Input
            label="Motivo eliminazione (opzionale)"
            type="text"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
          />
        </div>
      </Modal>

      {/* Modale rifiuto proposta (admin) */}
      <Modal
        isOpen={rejectOpen}
        closeButton={() => setRejectOpen(false)}
        headerText="Rifiuta proposta"
        size="medium"
        disableOverlayClose
        leftButtons={
          <Button
            className="delete-button"
            text="Rifiuta"
            type="button"
            icon={<MdClose className="button-icon" />}
            onClick={handleRifiuta}
          />
        }
      >
        <div className="flex flex-col gap-3 p-2">
          <p className="text-sm text-gray-500">
            {selectedLine?.ragione_sociale} — proposta:{" "}
            <strong className="text-amber-600">{fmt(selectedLine?.proposta_totale)} €</strong>
            {" "}(da {selectedLine?.proposta_by}){selectedLine?.proposta_motivo ? `: ${selectedLine.proposta_motivo}` : ""}
          </p>
          <Input
            label="Motivo rifiuto (opzionale)"
            type="text"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
          />
        </div>
      </Modal>

      <Modal isOpen={totaliOpen} size="compact" closeButton={() => setTotaliOpen(false)}
        headerText={<div className="text-center"><div>Totali per codice prestazione</div><div className="mt-0.5 text-xs font-normal text-gray-500">Altri adempimenti {String(mese).padStart(2, "0")}/{anno}</div></div>}>
        <div className="overflow-hidden rounded-md border border-slate-200">
          <div className="grid gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3 sm:grid-cols-[1fr_auto] sm:items-center">
            <div>
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Mostra tipologia</div>
              <div className="flex gap-2">
                {TIPOLOGIE_FATTURAZIONE_BUTTONS.map(({ t, active, idle }) => (
                  <button key={t} type="button" onClick={() => setTipologieTotali((current) => { const next = new Set(current); next.has(t) ? next.delete(t) : next.add(t); return next; })}
                    className={`px-2.5 py-0.5 rounded text-xs font-semibold border transition-colors ${tipologieTotali.has(t) ? active : idle}`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div className="rounded-xl border border-blue-100 bg-white px-5 py-3 text-right shadow-sm">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Totale selezionato</div>
              <div className="mt-0.5 whitespace-nowrap text-2xl font-bold text-blue-700">{fmt(totaleVoci)}&nbsp;€</div>
            </div>
          </div>
          <div className="max-h-[58vh] overflow-auto p-4">
            <table className="w-full border-collapse text-sm">
              <thead className="sticky top-0 z-10 bg-white text-xs uppercase text-gray-500"><tr className="border-b"><th className="px-3 py-2 text-left">Codice</th><th className="px-3 py-2 text-left">Descrizione</th><th className="px-3 py-2 text-right">Quantità</th><th className="px-3 py-2 text-right">Totale</th></tr></thead>
              <tbody>{totaliPerVoce.map((voce) => {
                const tipi = voce.tipologie; const rowClass = tipi.has("I") ? "border-l-emerald-500 bg-emerald-50/70" : tipi.has("B") ? "border-l-purple-500 bg-purple-50/70" : "border-l-blue-500 bg-blue-50/70";
                return <tr key={voce.codice} className={`border-b border-l-4 border-gray-100 ${rowClass}`}><td className="px-3 py-2 font-semibold text-blue-700">{voce.codice}</td><td className="px-3 py-2 text-gray-700">{voce.descrizione}</td><td className="px-3 py-2 text-right">{voce.quantita === 0 ? "—" : formatItalianNumber(voce.quantita, { maximumFractionDigits: 2 })}</td><td className="whitespace-nowrap px-3 py-2 text-right font-semibold">{voce.totale === 0 ? "—" : <>{fmt(voce.totale)}&nbsp;€</>}</td></tr>;
              })}</tbody>
            </table>
          </div>
        </div>
      </Modal>

      <DataGridPro
        className="min-h-0 flex-1"
        density="compact"
        slots={{
          toolbar: GridSlotStabile,
          loadingOverlay: LinearProgress,
          footer: GridSlotStabile,
        }}
        slotProps={{
          toolbar: { render: renderToolbar },
          footer: { render: renderFooter },
        }}
        loading={loading}
        localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
        filterModel={gridFilterModel}
        onFilterModelChange={setGridFilterModel}
        columns={columns}
        rows={lineeFiltrate}
        getRowId={(r) => r.id}
        getRowClassName={(p) =>
          isRigaEsclusa(p.row)
            ? "opacity-30 line-through"
            : p.row.proposta_totale != null
            ? "bg-amber-50"
            : ""
        }
      />
    </div>
  );
};

const ModalInserisciAdempimento = ({ periodo: sourcePeriodo, aziende, aziendePaghe = [], anno: sourceAnno, mese: sourceMese, lineaEsistente, tuttiTeam = false, onClose, onSaved }) => {
  const [aziendaSel, setAziendaSel] = useState(null);
  const [destination, setDestination] = useState(null);
  const [findingDestination, setFindingDestination] = useState(false);
  const [destinationRevision, setDestinationRevision] = useState(0);
  const periodo = destination?.periodo || sourcePeriodo;
  const anno = destination?.anno || sourceAnno;
  const mese = destination?.mese || sourceMese;
  const [aziendeCessate, setAziendeCessate] = useState([]);
  const [includeCessati, setIncludeCessati] = useState(() => localStorage.getItem(INCLUDE_CESSATI_KEY) === "true");
  const [tipologieFiltro, setTipologieFiltro] = useState(new Set());
  const [listino, setListino]       = useState([]);
  const [prestazioneSel, setPrestazioneSel] = useState(null);
  const [prestazioniOrderBy, setPrestazioniOrderBy] = useState("codice");
  const [settoreSel, setSettoreSel] = useState(() => getDefaultSettoreAltriAdempimenti());
  const [descrizione, setDescrizione] = useState("");
  const [province1415,setProvince1415]=useState('');
  const [tariffe1415,setTariffe1415]=useState(null);
  const [rateazione1422,setRateazione1422] = useState(false);
  const [presunto1422,setPresunto1422] = useState(false);
  const [parametri1422,setParametri1422] = useState(null);
  const [quantita, setQuantita]     = useState("");
  const [importoUnit, setImportoUnit] = useState("");
  const [totale, setTotale]         = useState("");
  const [note, setNote]             = useState("");
  const [notePerDiego, setNotePerDiego] = useState("");
  const [includeMeseCompetenza, setIncludeMeseCompetenza] = useState(false);
  const [integrazioneTipo, setIntegrazioneTipo] = useState("");
  const [integrazioneDipendenti, setIntegrazioneDipendenti] = useState("");
  const [integrazioneSettimane, setIntegrazioneSettimane] = useState("");
  const [rateazioneSubordinati, setRateazioneSubordinati] = useState("");
  const [rateazioneGestioneSeparata, setRateazioneGestioneSeparata] = useState("");
  const [rateazioneAltraGestione, setRateazioneAltraGestione] = useState("");
  const [rateazioneDomandeRespinte, setRateazioneDomandeRespinte] = useState(0);
  const [ricalcoloSpecialeVersion, setRicalcoloSpecialeVersion] = useState(0);
  const totaleCalcoloModificatoRef = useRef(!!lineaEsistente);
  const isEditMode = !!lineaEsistente;
  const canIncludeCessati = knowAcces(["parc_admin", "parc_team_leader", "parc_paghe", "parc_assunzioni", "parc_consulente"]);
  const isAdmin = knowAcces(["parc_admin"]);
  const settoriConsentiti = getSettoriAltriAdempimenti();
  useEffect(() => {
    let active = true;
    if (isEditMode || !aziendaSel) { setDestination(null); setFindingDestination(false); return () => { active = false; }; }
    const tipo = aziendaSel.tipologia_fatturazione;
    if (!adempimentiClosed(sourcePeriodo, tipo, isAdmin)) {
      setDestination({ periodo: sourcePeriodo, anno: sourceAnno, mese: sourceMese });
      setFindingDestination(false);
      return () => { active = false; };
    }
    setFindingDestination(true);
    (async () => {
      let nextAnno = sourceAnno, nextMese = sourceMese;
      for (let i = 0; i < 24; i += 1) {
        nextMese += 1;
        if (nextMese > 12) { nextMese = 1; nextAnno += 1; }
        const candidate = (await api.get(`/billing/periods/${nextAnno}/${nextMese}`)).data;
        if (!active) return;
        if (!adempimentiClosed(candidate, tipo, isAdmin)) {
          setDestination({ periodo: candidate, anno: nextAnno, mese: nextMese });
          return;
        }
      }
      if (active) { setDestination(null); toast.warning("Nessun mese aperto trovato nei prossimi 24 mesi"); }
    })().catch(() => { if (active) { setDestination(null); toast.error("Errore ricerca primo mese aperto"); } })
      .finally(() => { if (active) setFindingDestination(false); });
    return () => { active = false; };
  }, [sourcePeriodo, sourceAnno, sourceMese, aziendaSel, isAdmin, isEditMode, destinationRevision]);
  useAdempimentiRealtime({
    anno: destination?.anno, mese: destination?.mese,
    enabled: Boolean(!isEditMode && destination && (anno !== sourceAnno || mese !== sourceMese)),
    onChange: (event) => {
      if (event?.period || event?.action === "connected") setDestinationRevision(value => value + 1);
    },
  });

  const clienteDisponibilePaghe = !aziendaSel || aziendePaghe.some(
    (azienda) => azienda.codice_univoco === aziendaSel.codice_univoco
  ) || (canIncludeCessati && includeCessati && aziendeCessate.some(
    (azienda) => azienda.codice_univoco === aziendaSel.codice_univoco
  ));
  const settoriDisponibili = isEditMode
    ? settoriConsentiti
    : settoriConsentiti.filter((settore) => settore.id !== "PAGHE" || clienteDisponibilePaghe);

  const isVoceParticolare = !!prestazioneSel?.voce_particolare;
  const isCalcoloIntegrazioneSalariale = isIntegrazioneSalariale(prestazioneSel);
  const isCalcoloZucchetti1237 = isZucchetti1237(prestazioneSel);
  const isCalcoloBpoint1439 = isBpoint1439(prestazioneSel);
  const isCalcoloBpoint1415=String(prestazioneSel?.tipologia_fatturazione || aziendaSel?.tipologia_fatturazione)==='B' && Number(prestazioneSel?.nuovo_codice_ago)===1415;
  useEffect(()=>{
    setTariffe1415(null);
    if(!isCalcoloBpoint1415)return;
    let active=true;
    api.get('/billing/bpoint-listino',{params:{data_decorrenza:`${anno}-${String(mese).padStart(2,'0')}-01`}}).then(({data})=>{
      if(active)setTariffe1415({fasce:data.groups.find(g=>g.code===1415&&!g.parameter)?.rows || [],...Object.fromEntries(data.groups.filter(g=>g.code===1415&&g.parameter).map(g=>[g.field,g.rows[0].price]))});
    }).catch(()=>toast.error('Impossibile caricare le tariffe 1415'));
    return()=>{active=false};
  },[isCalcoloBpoint1415,anno,mese]);
  const fascia1415=tariffe1415?.fasce.find(row=>Number(quantita)>=row.min&&(row.max==null||Number(quantita)<=row.max));
  const totaleBpoint1415=isCalcoloBpoint1415 && fascia1415 && quantita!=='' && Number.isSafeInteger(Number(quantita)) && Number(quantita)>=1 && province1415!=='' && Number.isSafeInteger(Number(province1415)) && Number(province1415)>=0
    ? isPrestazioneAZero(prestazioneSel) ? 0 : Math.round(Math.min(Number(fascia1415.price)+Number(province1415)*Number(tariffe1415.quota_1415_provincia),Number(tariffe1415.tetto_1415))*100)/100 : null;
  const isCalcoloBpoint1422 = String(prestazioneSel?.tipologia_fatturazione || aziendaSel?.tipologia_fatturazione) === 'B' && Number(prestazioneSel?.nuovo_codice_ago) === 1422;
  useEffect(() => {
    if (!isCalcoloBpoint1422) {setParametri1422(null);return;}
    let active=true;
    api.get('/billing/bpoint-listino',{params:{data_decorrenza:`${anno}-${String(mese).padStart(2,'0')}-01`}}).then(({data})=>{
      if(active)setParametri1422(Object.fromEntries(data.groups.filter(g=>g.code===1422).map(g=>[g.field,g.rows[0].price])));
    }).catch(()=>toast.error('Impossibile caricare le tariffe 1422'));
    return()=>{active=false};
  },[isCalcoloBpoint1422,anno,mese]);
  const totaleBpoint1422 = isCalcoloBpoint1422 && parametri1422 && quantita !== '' && Number.isSafeInteger(Number(quantita)) && Number(quantita)>=0
    ? isPrestazioneAZero(prestazioneSel) ? 0 : Number(quantita)*Number(prestazioneSel?.importo ?? parametri1422.quota_1422_quantita)+(rateazione1422?Number(parametri1422.quota_1422_rateazione):0)+(presunto1422?Number(parametri1422.quota_1422_presunto):0) : null;
  const isCalcoloSpeciale = isCalcoloIntegrazioneSalariale || isCalcoloBpoint1439 || isCalcoloBpoint1422 || isCalcoloBpoint1415;
  const isIipayRateCard = Number(prestazioneSel?.iipay_rate_card) === 1;
  const mancaCedoliniIipay = Number(prestazioneSel?.iipay_manca_cedolini_precedenti) === 1;
  const cedoliniIipayPresunti = Number(prestazioneSel?.iipay_cedolini_presunti) === 1;
  const iipayFasciaLabel = getIipayFasciaApplicataLabel(prestazioneSel);
  // Per iiPay "Tabella" e "Fascia" condividono metodo_fatturazione = 6: una voce a
  // fascia prende comunque il prezzo dalla rate card, quindi va bloccata come le voci
  // a listino e non trattata come voce a tabella con importo libero.
  const voceAFasciaIipay = isIipayVoceAFascia(prestazioneSel);
  const isIipayLiberaOTabella =
    [1, 2, 6, 8].includes(Number(prestazioneSel?.metodo_fatturazione)) && !voceAFasciaIipay;
  const lockImportiIipay = isIipayRateCard && !isIipayLiberaOTabella && !isAdmin;
  const lockImportiAZero = isPrestazioneAZero(prestazioneSel);
  const codicePrestazioneSelezionata = String(
    prestazioneSel?.nuovo_codice_ago || prestazioneSel?.codice || ""
  ).trim().replace(/^0+(?=\d)/, "");
  const isMillerConListinoOrario40 = codicePrestazioneSelezionata === "1222"
    && new Set(["14", "15", "729"]).has(String(aziendaSel?.codice_parcellazione || "").trim());
  const notaListino = isMillerConListinoOrario40
    ? "listino orario 40 euro/ora"
    : String(prestazioneSel?.note || "").trim();
  const notaVicinoQuantita = codicePrestazioneSelezionata === "443";
  const isDescrizioneInternaObbligatoria = includeMeseCompetenza;
  const mostraQuantita = deveMostrareQuantitaListino(prestazioneSel);
  const importoListinoVariato = mostraQuantita
    ? isImportoDiversoDaListino(prestazioneSel, importoUnit)
    : isTotaleDiversoDaListino(prestazioneSel, totale);
  const richiestaRaw = prestazioneSel?.richiesta || null;
  // Il mese di competenza automatico aggiunge una descrizione interna, ma non
  // sostituisce l'eventuale descrizione in fattura richiesta dalla prestazione.
  const isRichiestaObbligatoria = isCalcoloIntegrazioneSalariale || !!String(richiestaRaw || '').trim();
  const richiestaHint = isCalcoloIntegrazioneSalariale
    ? "Inserire periodo dal … al …"
    : formatRichiestaDescrizioneLabel(richiestaRaw);
  const totaleIntegrazioneSalariale = isCalcoloIntegrazioneSalariale
    ? calculateBpoint1427({
        tipo: integrazioneTipo,
        dipendenti: integrazioneDipendenti,
        settimane: integrazioneSettimane,
        parametri: prestazioneSel,
      })
    : null;
  const totaleBpoint1439 = isCalcoloBpoint1439
    ? calculateBpoint1439({ subordinati: rateazioneSubordinati, gestioneSeparata: rateazioneGestioneSeparata,
        altraGestione: rateazioneAltraGestione, domandeRespinte: rateazioneDomandeRespinte, parametri: prestazioneSel })
    : null;
  const totaleCalcolatoSpeciale = isCalcoloIntegrazioneSalariale ? totaleIntegrazioneSalariale : isCalcoloBpoint1439 ? totaleBpoint1439 : isCalcoloBpoint1422 ? totaleBpoint1422 : isCalcoloBpoint1415 ? totaleBpoint1415 : null;
  const totaleSpecialeForzato = isCalcoloSpeciale && totale !== "" && totale != null &&
    totaleCalcolatoSpeciale != null && Math.abs(Number(totale) - Number(totaleCalcolatoSpeciale)) > 0.005;
  const importoVariato = isCalcoloSpeciale ? totaleSpecialeForzato : importoListinoVariato;

  useEffect(() => {
    const calcolato = isCalcoloIntegrazioneSalariale ? totaleIntegrazioneSalariale : isCalcoloBpoint1439 ? totaleBpoint1439 : isCalcoloBpoint1422 ? totaleBpoint1422 : isCalcoloBpoint1415 ? totaleBpoint1415 : null;
    if (calcolato != null && !totaleCalcoloModificatoRef.current) setTotale(calcolato);
  }, [isCalcoloIntegrazioneSalariale, isCalcoloBpoint1439, totaleIntegrazioneSalariale, totaleBpoint1439, isCalcoloBpoint1422, totaleBpoint1422, isCalcoloBpoint1415, totaleBpoint1415, ricalcoloSpecialeVersion]);

  const richiediRicalcoloSpeciale = () => {
    totaleCalcoloModificatoRef.current = false;
    setRicalcoloSpecialeVersion((versione) => versione + 1);
  };

  const loadListino = useCallback(async (codice, settore) => {
    try {
      const params = new URLSearchParams({ sezione: "3" });
      if (settore) params.set("settore", settore);
      if (anno) params.set("anno", anno);
      if (mese) params.set("mese", mese);
      const res = await api.get(`/prestazione_azienda/listino/${codice}?${params.toString()}`);
      setListino(res.data);
      return res.data;
    } catch (err) {
      setListino([]);
      toast.error(err.response?.data?.message || "Errore caricamento listino prestazioni");
      return [];
    }
  }, [anno, mese]);

  const loadAziendeCessate = async () => {
    try {
      const res = await api.get("/aziende/parcellazione/cessati", { params: { tutti: tuttiTeam ? "1" : "0" } });
      setAziendeCessate(res.data || []);
    } catch {
      setAziendeCessate([]);
      toast.error("Errore caricamento clienti cessati");
    }
  };

  const aziendePerSettore = settoreSel === "PAGHE" ? aziendePaghe : aziende;
  const aziendeSelezionabili = (canIncludeCessati && includeCessati ? [...aziendePerSettore, ...aziendeCessate] : aziendePerSettore)
    .filter((a, i, arr) => arr.findIndex((b) => b.codice_univoco === a.codice_univoco) === i)
    .filter((a) => String(a.tipologia_fatturazione || '').trim().toUpperCase() !== 'G')
    .filter((a) => tipologieFiltro.size === 0 || tipologieFiltro.has(String(a.tipologia_fatturazione || "").toUpperCase()))
    .sort((a, b) => a.ragione_sociale.localeCompare(b.ragione_sociale));

  const prestazioniOptions = useMemo(() => [...listino]
    .sort((a, b) => {
      if (prestazioniOrderBy === "codice") {
        const codiceA = getCodicePrestazioneSort(a);
        const codiceB = getCodicePrestazioneSort(b);
        if (typeof codiceA === "number" && typeof codiceB === "number") return codiceA - codiceB;
        return String(codiceA).localeCompare(String(codiceB), "it", { numeric: true, sensitivity: "base" });
      }
      const descrizioneCompare = String(a.descrizione || "").localeCompare(String(b.descrizione || ""), "it", {
        numeric: true,
        sensitivity: "base",
      });
      if (descrizioneCompare !== 0) return descrizioneCompare;
      const codiceA = getCodicePrestazioneSort(a);
      const codiceB = getCodicePrestazioneSort(b);
      if (typeof codiceA === "number" && typeof codiceB === "number") return codiceA - codiceB;
      return String(codiceA).localeCompare(String(codiceB), "it", { numeric: true, sensitivity: "base" });
    })
    .map((l) => ({
      id: l.codice,
      name: formatPrestazioneListinoName(l),
      badges: getPrestazioneListinoBadges(l, settoreSel),
    })), [listino, prestazioniOrderBy, settoreSel]);
  const prestazioniDisabilitate = !settoreSel || isEditMode || prestazioniOptions.length === 0;

  const setImportiDaPrestazione = (prestazione, quantitaAttuale = quantita) => {
    if (!prestazione) {
      setQuantita("");
      setImportoUnit("");
      setTotale("");
      return;
    }

    const hasQuantita = deveMostrareQuantitaListino(prestazione);
    const importoDefault = getImportoListinoDefault(prestazione);
    if (isPrestazioneAZero(prestazione)) {
      if (hasQuantita) {
        const qta = quantitaAttuale !== "" && quantitaAttuale != null ? quantitaAttuale : 1;
        setQuantita(qta);
        setImportoUnit(0);
      } else {
        setQuantita("");
        setImportoUnit("");
      }
      setTotale(0);
      return;
    }
    if (hasQuantita) {
      const qta = quantitaAttuale !== "" && quantitaAttuale != null ? quantitaAttuale : 1;
      setQuantita(qta);
      setImportoUnit(importoDefault);
      setTotale((parseFloat(qta) * parseFloat(importoDefault || 0)) || 0);
    } else {
      setQuantita("");
      setImportoUnit("");
      setTotale(importoDefault);
    }
  };

  // Pre-carica dati se in modalità edit
  useEffect(() => {
    if (!lineaEsistente) return;
    // usa fallback con i dati della riga se l'azienda è cessata e non è nella lista attivi
    const az = aziende.find((a) => a.codice_univoco === lineaEsistente.codice_univoco_azienda)
      || { codice_univoco: lineaEsistente.codice_univoco_azienda, ragione_sociale: lineaEsistente.ragione_sociale };
    const settoreRiga = lineaEsistente.settore || "";
    setAziendaSel(az);
    setSettoreSel(settoreRiga || getDefaultSettoreAltriAdempimenti());
    setDescrizione(lineaEsistente.descrizione_extra_fattura || "");
    setRateazione1422(Number(lineaEsistente.bpoint_1422_rateazione)===1); setPresunto1422(Number(lineaEsistente.bpoint_1422_presunto)===1);
    setProvince1415(lineaEsistente.bpoint_1415_province ?? '');
    setQuantita(lineaEsistente.bpoint_1415_dipendenti ?? lineaEsistente.bpoint_1422_quantita ?? lineaEsistente.quantita ?? "");
    setImportoUnit(lineaEsistente.importo_unitario ?? "");
    setTotale(lineaEsistente.totale ?? "");
    setNote(lineaEsistente.descrizione_interna || "");
    setNotePerDiego(lineaEsistente.note_admin || "");
    setIntegrazioneTipo(lineaEsistente.integrazione_tipo || "");
    setIntegrazioneDipendenti(lineaEsistente.integrazione_dipendenti ?? "");
    setIntegrazioneSettimane(lineaEsistente.integrazione_settimane ?? "");
    setRateazioneSubordinati(lineaEsistente.rateazione_subordinati || "");
    setRateazioneGestioneSeparata(lineaEsistente.rateazione_gestione_separata || "");
    setRateazioneAltraGestione(lineaEsistente.rateazione_altra_gestione || "");
    setRateazioneDomandeRespinte(lineaEsistente.rateazione_domande_respinte ?? 0);
    setIncludeMeseCompetenza(
      String(lineaEsistente.descrizione_interna || "")
        .toLowerCase()
        .includes(formatMeseCompetenza(mese, anno).toLowerCase()) ||
      isClienteMeseCompetenzaAutomatico(az)
    );
    if (az) {
      loadListino(az.codice_univoco, settoreRiga || null).then((lst) => {
        const p = lst.find((l) => String(l.codice) === String(lineaEsistente.prestazione))
          || buildPrestazioneFromLine(lineaEsistente);
        setPrestazioneSel(p);
        setImportoUnit(normalizeImportoSconto(p, lineaEsistente.importo_unitario ?? ""));
        setTotale(normalizeImportoSconto(p, lineaEsistente.totale ?? ""));
        if (!settoreRiga && p?.settore_listino && settoriConsentiti.some((s) => s.id === p.settore_listino)) {
          setSettoreSel(p.settore_listino);
        }
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineaEsistente]);

  useEffect(() => {
    localStorage.setItem(INCLUDE_CESSATI_KEY, String(includeCessati));
    if (canIncludeCessati && includeCessati && aziendeCessate.length === 0) loadAziendeCessate();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeCessati, canIncludeCessati]);

  useEffect(() => {
    if (!settoreSel && settoriConsentiti.length > 0) setSettoreSel(getDefaultSettoreAltriAdempimenti());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settoreSel]);

  useEffect(() => {
    if (isEditMode) return;
    if (!aziendaSel?.codice_univoco || !settoreSel) {
      setListino([]);
      return;
    }
    loadListino(aziendaSel.codice_univoco, settoreSel);
  }, [aziendaSel?.codice_univoco, settoreSel, isEditMode, loadListino]);

  const handleChangeAzienda = async (e) => {
    const codice = e.target.value;
    const az = aziendeSelezionabili.find((a) => a.codice_univoco === codice) || null;
    setAziendaSel(az);
    setPrestazioneSel(null);
    setListino([]);
    setDescrizione(""); setQuantita(""); setImportoUnit(""); setTotale(""); setNote(""); setNotePerDiego("");
    setProvince1415('');
    setRateazione1422(false);setPresunto1422(false);
    setIntegrazioneTipo(""); setIntegrazioneDipendenti(""); setIntegrazioneSettimane("");
    setRateazioneSubordinati(""); setRateazioneGestioneSeparata(""); setRateazioneAltraGestione(""); setRateazioneDomandeRespinte(0);
    totaleCalcoloModificatoRef.current = false;
    setIncludeMeseCompetenza(isClienteMeseCompetenzaAutomatico(az));
  };

  const handleChangeSettore = async (e) => {
    const settore = e.target.value;
    setSettoreSel(settore);
    const cessateAmmesse = canIncludeCessati && includeCessati ? aziendeCessate : [];
    const aziendeAmmesse = settore === "PAGHE"
      ? [...aziendePaghe, ...cessateAmmesse]
      : [...aziende, ...cessateAmmesse];
    if (aziendaSel && !aziendeAmmesse.some((a) => a.codice_univoco === aziendaSel.codice_univoco)) {
      setAziendaSel(null);
    }
    setPrestazioneSel(null);
    setListino([]);
    setDescrizione(""); setQuantita(""); setImportoUnit(""); setTotale(""); setNotePerDiego("");
    setProvince1415('');
    setRateazione1422(false);setPresunto1422(false);
    setIntegrazioneTipo(""); setIntegrazioneDipendenti(""); setIntegrazioneSettimane("");
    setRateazioneSubordinati(""); setRateazioneGestioneSeparata(""); setRateazioneAltraGestione(""); setRateazioneDomandeRespinte(0);
    totaleCalcoloModificatoRef.current = false;
  };

  const handleChangePrestazione = (e) => {
    const codice = e.target.value;
    const p = listino.find((l) => String(l.codice) === String(codice)) || null;
    setPrestazioneSel(p);
    setDescrizione("");
    setNote("");
    setNotePerDiego("");
    setProvince1415('');
    setRateazione1422(false);setPresunto1422(false);
    setIntegrazioneTipo("");
    setIntegrazioneDipendenti("");
    setIntegrazioneSettimane("");
    setRateazioneSubordinati(""); setRateazioneGestioneSeparata(""); setRateazioneAltraGestione(""); setRateazioneDomandeRespinte(0);
    totaleCalcoloModificatoRef.current = false;
    setImportiDaPrestazione(p, 1);
  };

  const handleQtaChange = (e) => {
    const q = e.target.value;
    setQuantita(q);
    if (isPrestazioneAZero(prestazioneSel)) {
      setImportoUnit(0);
      setTotale(0);
      return;
    }
    if (importoUnit !== "" && importoUnit != null) {
      const imp = normalizeImportoSconto(prestazioneSel, importoUnit);
      setTotale(q === "" ? "" : (parseFloat(q) * parseFloat(imp)) || 0);
    }
  };

  const handleImportoChange = (e) => {
    if (isPrestazioneAZero(prestazioneSel)) {
      setImportoUnit(0);
      setTotale(0);
      return;
    }
    const i = normalizeImportoSconto(prestazioneSel, e.target.value);
    setImportoUnit(i);
    if (quantita !== "" && quantita != null) {
      setTotale(i === "" ? "" : (parseFloat(quantita) * parseFloat(i)) || 0);
    }
  };

  const findFirstOpenPeriod = async () => {
    let nextAnno = anno;
    let nextMese = mese;
    for (let i = 0; i < 24; i += 1) {
      nextMese += 1;
      if (nextMese > 12) {
        nextMese = 1;
        nextAnno += 1;
      }
      const res = await api.get(`/billing/periods/${nextAnno}/${nextMese}`);
      const candidate = res.data;
      const candidateBloccato = adempimentiClosed(candidate, aziendaSel?.tipologia_fatturazione, isAdmin);
      if (!candidateBloccato) return { periodo: candidate, anno: nextAnno, mese: nextMese };
    }
    return null;
  };

  const saveInsertPayload = async (payload, target = { periodo, anno, mese }) => {
    await api.post("/billing/lines", {
      ...payload,
      billing_period_id: target.periodo.id,
      mese_competenza: target.mese,
      anno_competenza: target.anno,
    });
  };

  const handleClosedPeriodSave = async (payload) => {
    const target = await findFirstOpenPeriod();
    if (!target) {
      toast.warning("Nessun mese aperto trovato nei prossimi 24 mesi");
      return;
    }
    const label = `${String(target.mese).padStart(2, "0")}/${target.anno}`;
    const confirmed = window.confirm(
      `Il mese selezionato è stato chiuso.\n\nVuoi salvare questo adempimento nel primo mese aperto disponibile (${label})?`
    );
    if (!confirmed) return;
    await saveInsertPayload(payload, target);
    toast.success(`Adempimento inserito in ${label}`);
    onSaved(target);
  };

  const handleSave = async () => {
    if (!aziendaSel) { toast.warning("Seleziona un cliente"); return; }
    if (!isEditMode && (findingDestination || !destination)) { toast.info("Attendi la ricerca del primo mese utile"); return; }
    if (isEditMode && adempimentiClosed(periodo, aziendaSel.tipologia_fatturazione, isAdmin)) { toast.info("Sezione chiusa per questo gestionale"); return; }
    if (!settoreSel) { toast.warning("Seleziona un settore"); return; }
    if (!prestazioneSel) { toast.warning("Seleziona una prestazione"); return; }
    if (isCalcoloIntegrazioneSalariale && totaleIntegrazioneSalariale == null) {
      toast.warning("Seleziona il tipo e inserisci dipendenti e settimane"); return;
    }
    if(isCalcoloBpoint1415 && totaleBpoint1415==null){toast.warning('Inserisci il numero di dipendenti e province e attendi le tariffe');return;}
    if (isCalcoloBpoint1422 && totaleBpoint1422 == null) {toast.warning("Inserisci la quantità (numero voci di rischio) e attendi le tariffe");return;}
    if (isCalcoloBpoint1439 && totaleBpoint1439 == null) { toast.warning("Completa le tre fasce e il numero di domande respinte"); return; }
    if (totale === "" || totale == null) { toast.warning("Inserisci il totale"); return; }
    if (!isCalcoloSpeciale && mostraQuantita && (quantita === "" || quantita == null)) {
      toast.warning("Inserisci la quantità"); return;
    }
    if (isRichiestaObbligatoria && !descrizione.trim()) {
      toast.warning("Inserisci la descrizione aggiuntiva in fattura");
      return;
    }
    if (mancaCedoliniIipay) {
      toast.warning(
        isAdmin
          ? "Inserisci i cedolini presunti dal riquadro Cedolini rate card prima di salvare l'adempimento"
          : "Cedolini iiPay del mese precedente mancanti: inserire i cedolini presunti o importare il mese precedente"
      );
      return;
    }

    const qta = (!isCalcoloSpeciale && mostraQuantita) && quantita !== "" && quantita != null ? parseFloat(quantita) : null;
    const aZero = isPrestazioneAZero(prestazioneSel);
    const impUnit = aZero && mostraQuantita
      ? 0
      : mostraQuantita && importoUnit !== "" && importoUnit != null
      ? normalizeImportoSconto(prestazioneSel, parseFloat(importoUnit))
      : null;
    const totaleFinale = aZero && !isCalcoloSpeciale ? 0 : normalizeImportoSconto(prestazioneSel, parseFloat(totale));
    const totaleCalcolato = isCalcoloIntegrazioneSalariale ? totaleIntegrazioneSalariale : isCalcoloBpoint1439 ? totaleBpoint1439 : isCalcoloBpoint1422 ? totaleBpoint1422 : isCalcoloBpoint1415 ? totaleBpoint1415 : null;
    const calcoloSpecialeVariato = isCalcoloSpeciale && Math.abs(Number(totaleFinale) - Number(totaleCalcolato)) > 0.005;
    const richiedeConfermaImporto = !isAdmin && (calcoloSpecialeVariato || (!isCalcoloSpeciale && (
      mostraQuantita
        ? isImportoDiversoDaListino(prestazioneSel, impUnit)
        : isTotaleDiversoDaListino(prestazioneSel, totaleFinale)
    )));
    const statoForzatoAdmin = isAdmin && (calcoloSpecialeVariato || (!isCalcoloSpeciale && (
      mostraQuantita
        ? isImportoDiversoDaListino(prestazioneSel, impUnit)
        : isTotaleDiversoDaListino(prestazioneSel, totaleFinale)
    )));
    const noteFinali = includeMeseCompetenza ? mergeMeseCompetenzaNote(note, mese, anno) : note.trim();
    if (isDescrizioneInternaObbligatoria && !noteFinali) {
      toast.warning("Inserisci la descrizione non in fattura");
      return;
    }
    if (!isCalcoloSpeciale && mostraQuantita && isImportoZeroVoceATabella(prestazioneSel, impUnit)) {
      const confirmed = window.confirm(
        `La prestazione selezionata e' a tabella e l'importo unitario e' 0,00 €.\n\n` +
        "Confermi il salvataggio?"
      );
      if (!confirmed) return;
    }

    let insertPayload = null;
    try {
      if (isEditMode) {
        const lineId = lineaEsistente.original_ids?.[0] ?? lineaEsistente.id;
        if (isAdmin) {
          const confirmed = window.confirm(
            `Confermi la modifica dell'adempimento per ${aziendaSel.ragione_sociale}?\n\n` +
            `Totale: ${formatItalianNumber(totaleFinale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
          );
          if (!confirmed) return;
        }
        await api.put(`/billing/lines/${lineId}`, {
          ...(isCalcoloBpoint1415 ? {bpoint_1415_dipendenti:Number(quantita),bpoint_1415_province:Number(province1415)} : {}),
          ...(isCalcoloBpoint1422 ? {bpoint_1422_quantita:Number(quantita),bpoint_1422_rateazione:rateazione1422?1:0,bpoint_1422_presunto:presunto1422?1:0} : {}),
          quantita:                  qta,
          importo_unitario:          impUnit,
          totale:                    totaleFinale,
          descrizione_extra_fattura: descrizione || null,
          descrizione_interna:       noteFinali || null,
          note_admin:                 notePerDiego.trim() || null,
          ...(isCalcoloIntegrazioneSalariale ? {
            integrazione_tipo: integrazioneTipo,
            integrazione_dipendenti: Number(integrazioneDipendenti),
            integrazione_settimane: Number(integrazioneSettimane),
          } : {}),
          ...(isCalcoloBpoint1439 ? { rateazione_subordinati: rateazioneSubordinati,
            rateazione_gestione_separata: rateazioneGestioneSeparata, rateazione_altra_gestione: rateazioneAltraGestione,
            rateazione_domande_respinte: Number(rateazioneDomandeRespinte) } : {}),
          ...(isAdmin && statoForzatoAdmin ? { stato: "forzata" } : {}),
        });
        toast.success(
          isAdmin || !richiedeConfermaImporto
            ? "Adempimento aggiornato"
            : "Adempimento aggiornato — in attesa di validazione ADMIN"
        );
      } else {
        insertPayload = {
          sezione:                   "altri_adempimenti",
          codice_univoco_azienda:    aziendaSel.codice_univoco,
          prestazione:               prestazioneSel?.codice || null,
          settore:                   settoreSel,
          origine:                   "manuale",
          ...(isCalcoloBpoint1415 ? {bpoint_1415_dipendenti:Number(quantita),bpoint_1415_province:Number(province1415)} : {}),
          ...(isCalcoloBpoint1422 ? {bpoint_1422_quantita:Number(quantita),bpoint_1422_rateazione:rateazione1422?1:0,bpoint_1422_presunto:presunto1422?1:0} : {}),
          quantita:                  qta,
          importo_unitario:          impUnit,
          totale:                    totaleFinale,
          descrizione_extra_fattura: descrizione || null,
          descrizione_interna:       noteFinali || null,
          note_admin:                 notePerDiego.trim() || null,
          ...(isCalcoloIntegrazioneSalariale ? {
            integrazione_tipo: integrazioneTipo,
            integrazione_dipendenti: Number(integrazioneDipendenti),
            integrazione_settimane: Number(integrazioneSettimane),
          } : {}),
          ...(isCalcoloBpoint1439 ? { rateazione_subordinati: rateazioneSubordinati,
            rateazione_gestione_separata: rateazioneGestioneSeparata, rateazione_altra_gestione: rateazioneAltraGestione,
            rateazione_domande_respinte: Number(rateazioneDomandeRespinte) } : {}),
          mese_competenza:           mese,
          anno_competenza:           anno,
          stato:                     statoForzatoAdmin ? "forzata" : richiedeConfermaImporto ? "bozza" : "validata",
          tutti_team:                tuttiTeam,
        };
        await saveInsertPayload(insertPayload);
        toast.success(richiedeConfermaImporto ? "Adempimento inserito — importo da confermare" : "Adempimento inserito");
      }
      onSaved(isEditMode ? null : destination);
    } catch (err) {
      const status = err.response?.status;
      const message = String(err.response?.data?.message || "");
      if (!isEditMode && insertPayload && status === 409 && /chius/i.test(message)) {
        try {
          await handleClosedPeriodSave(insertPayload);
        } catch {
          toast.error("Errore salvataggio nel primo mese aperto");
        }
        return;
      }
      toast.error(err.response?.data?.message || (isEditMode ? "Errore aggiornamento" : "Errore inserimento adempimento"));
    }
  };

  return (
    <Modal
      isOpen={true}
      closeButton={onClose}
      headerText={
        isEditMode
          ? `Modifica adempimento — ${String(mese).padStart(2, "0")}/${anno}`
          : `Nuovo adempimento — ${String(mese).padStart(2, "0")}/${anno}`
      }
      disableOverlayClose
      leftButtons={
        <Button
          className="principal-button"
          text="Salva"
          disabled={isEditMode ? adempimentiClosed(periodo, aziendaSel?.tipologia_fatturazione, isAdmin) : findingDestination || !destination}
          type="button"
          icon={<HiOutlineCheck className="button-icon" />}
          onClick={handleSave}
        />
      }
    >
      {!isEditMode && aziendaSel && findingDestination && <p role="status" className="mb-3 text-sm text-gray-600">Ricerca del primo mese utile…</p>}
      {!isEditMode && !findingDestination && destination && (anno !== sourceAnno || mese !== sourceMese) && <p role="status" className="mb-3 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
        La sezione {aziendaSel?.tipologia_fatturazione === "B" ? "BPoint" : "Zucchetti/iiPay"} di {String(sourceMese).padStart(2, "0")}/{sourceAnno} è chiusa. L’adempimento sarà inserito nel primo mese utile: <strong>{String(mese).padStart(2, "0")}/{anno}</strong>.
      </p>}
      <div className="grid grid-cols-2 gap-3">
        {canIncludeCessati && !isEditMode && (
          <div className="col-span-2 flex items-center justify-end">
            <label className="inline-flex items-center gap-2 text-xs font-medium text-gray-600 uppercase">
              <input
                type="checkbox"
                checked={includeCessati}
                onChange={(e) => setIncludeCessati(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600"
              />
              Includi cessati
            </label>
          </div>
        )}
        <div className="col-span-2">
          {!isEditMode && (
            <div className="flex items-center gap-1.5 mb-1.5">
              {TIPOLOGIE_FATTURAZIONE_BUTTONS.map(({ t, active, idle }) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTipologieFiltro((prev) => { const next = new Set(prev); next.has(t) ? next.delete(t) : next.add(t); return next; })}
                  className={`px-2.5 py-0.5 rounded text-xs font-semibold border transition-colors ${tipologieFiltro.has(t) ? active : idle}`}
                >
                  {t}
                </button>
              ))}
            </div>
          )}
          <Autocomplete
            label="Cliente"
            placeholder="Cerca cliente..."
            value={aziendaSel?.codice_univoco || ""}
            onChange={handleChangeAzienda}
            options={aziendeSelezionabili.map((a) => ({
              id: a.codice_univoco,
              name: `${a.ragione_sociale}${a.data_fine_elaborazioni ? " (cessato)" : ""}`,
              badge: a.tipologia_fatturazione,
            }))}
            disabled={isEditMode}
          />
        </div>
        {aziendaSel && (
          <>
            <div className="col-span-2 md:col-span-1">
              <Autocomplete
                label="Settore listino"
                placeholder="Seleziona..."
                value={settoreSel}
                onChange={handleChangeSettore}
                options={settoriDisponibili}
                disabled={isEditMode || settoriDisponibili.length <= 1}
              />
            </div>
            <div className="col-span-2 md:col-span-1 relative">
              <div className="absolute right-0 top-0 z-10 flex items-center justify-end gap-1">
                {[
                  { id: "descrizione", label: "A-Z" },
                  { id: "codice", label: "Cod." },
                ].map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    disabled={prestazioniDisabilitate}
                    onClick={() => setPrestazioniOrderBy(item.id)}
                    className={`px-2 py-0.5 rounded text-[11px] font-semibold border transition-colors ${
                      prestazioniDisabilitate
                        ? "cursor-not-allowed bg-gray-100 text-gray-400 border-gray-200"
                        : prestazioniOrderBy === item.id
                        ? "bg-blue-50 text-blue-700 border-blue-300"
                        : "bg-white text-gray-500 border-gray-200"
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              <Autocomplete
                label="Prestazione (dal listino)"
                placeholder="Cerca prestazione..."
                value={prestazioneSel?.codice || ""}
                onChange={handleChangePrestazione}
                options={prestazioniOptions}
                badgePosition="right"
                disabled={prestazioniDisabilitate}
              />
            </div>
          </>
        )}
        {isVoceParticolare && (
          <div className="col-span-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <span className="font-semibold">Voce particolare:</span>{" "}
            inserisci solo il totale libero. Quantità e importo unitario non vanno in fattura.
          </div>
        )}
        {isIipayRateCard && (
          <div className="col-span-2 rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-900">
            <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
              <div>
                <div className="text-[11px] font-semibold uppercase text-blue-500">Cedolini considerati</div>
                <div className="font-semibold">
                  {mancaCedoliniIipay
                    ? "mancanti"
                    : `${prestazioneSel?.iipay_cedolini_mese_precedente ?? "—"} cedolini${cedoliniIipayPresunti ? " (presunti)" : ""}`}
                </div>
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase text-blue-500">Mese dati cedolini</div>
                <div className="font-semibold capitalize">
                  {formatPeriodoCedolini(prestazioneSel?.iipay_cedolini_data_considerata)}
                </div>
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase text-blue-500">Fascia rate card</div>
                <div className="font-semibold">{mancaCedoliniIipay ? "non calcolabile" : iipayFasciaLabel || "—"}</div>
              </div>
            </div>
            {mancaCedoliniIipay ? (
              <div className="mt-2 text-xs font-medium text-blue-700">
                Inserisci i cedolini presunti dal riquadro Cedolini rate card per calcolare la fascia.
              </div>
            ) : null}
          </div>
        )}
        {isCalcoloIntegrazioneSalariale ? (
          <div className={`col-span-2 rounded-md border p-3 ${isCalcoloZucchetti1237 ? "border-blue-200 bg-blue-50/50" : "border-purple-200 bg-purple-50/50"}`}>
            <div className={`mb-2 text-xs font-semibold uppercase tracking-wider ${isCalcoloZucchetti1237 ? "text-blue-700" : "text-purple-700"}`}>
              Calcolo integrazione salariale
            </div>
            <div className="mb-3 flex flex-wrap gap-2">
              {BPOINT_1427_TYPES.map((tipo) => (
                <button
                  key={tipo.id}
                  type="button"
                  onClick={() => { richiediRicalcoloSpeciale(); setIntegrazioneTipo(tipo.id); }}
                  className={`rounded-md border px-3 py-1.5 text-sm font-semibold transition-colors ${
                    integrazioneTipo === tipo.id
                      ? isCalcoloZucchetti1237
                        ? "border-blue-400 bg-blue-100 text-blue-800 ring-1 ring-blue-300"
                        : "border-purple-400 bg-purple-100 text-purple-800 ring-1 ring-purple-300"
                      : isCalcoloZucchetti1237
                        ? "border-gray-200 bg-white text-gray-600 hover:border-blue-300"
                        : "border-gray-200 bg-white text-gray-600 hover:border-purple-300"
                  }`}
                >
                  {tipo.name}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <Input
                label="N. dipendenti"
                type="number"
                min="1"
                step="1"
                value={integrazioneDipendenti}
                onChange={(e) => { richiediRicalcoloSpeciale(); setIntegrazioneDipendenti(e.target.value); }}
              />
              <Input
                label="N. settimane"
                type="number"
                min="1"
                step="1"
                value={integrazioneSettimane}
                onChange={(e) => { richiediRicalcoloSpeciale(); setIntegrazioneSettimane(e.target.value); }}
              />
              <Input
                label="Totale proposto (€)"
                type="number"
                value={totale}
                onChange={(e) => { totaleCalcoloModificatoRef.current = true; setTotale(e.target.value); }}
              />
            </div>
            <div className="mt-2 text-xs text-gray-500">
              {integrazioneTipo === "FSBA"
                ? `FSBA: ${fmt(prestazioneSel.fsba_coefficiente)} € × dipendenti × settimane; arrotondamento per eccesso a 5 €, minimo ${fmt(prestazioneSel.fsba_minimo)} €, massimo ${fmt(prestazioneSel.tetto_massimo)} €.`
                : integrazioneTipo
                ? `${integrazioneTipo === "CIGO" ? "CIGO" : "CIGO Maltempo"}: quota base ${fmt(integrazioneTipo === "CIGO" ? prestazioneSel.cigo_quota_base : prestazioneSel.cigo_maltempo_quota_base)} € + ${fmt(prestazioneSel.quota_variabile)} € × dipendenti × settimane; arrotondamento a 5 €, massimo ${fmt(prestazioneSel.tetto_massimo)} €.`
                : "Seleziona prima il tipo di pratica."}
            </div>
          </div>
        ) : isCalcoloBpoint1415 ? (
          <div className="col-span-2 rounded-md border border-purple-200 bg-purple-50/50 p-3">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Input label="Quantità: numero dipendenti (computabili)" type="number" value={quantita} onChange={e=>{setQuantita(e.target.value);richiediRicalcoloSpeciale();}} />
              <Input label="Numero province" type="number" value={province1415} onChange={e=>{setProvince1415(e.target.value);richiediRicalcoloSpeciale();}} />
              <Input label="Totale (€)" type="number" value={totale} onChange={e=>{totaleCalcoloModificatoRef.current=true;setTotale(e.target.value);}} />
            </div>
            {fascia1415 && <div className="mt-2 text-xs text-gray-500">Quota base {fmt(fascia1415.price)} € + {fmt(tariffe1415.quota_1415_provincia)} € per provincia; massimo complessivo {fmt(tariffe1415.tetto_1415)} €.</div>}
          </div>
        ) : isCalcoloBpoint1422 ? (
          <>
            <Input label="Quantità (numero voci di rischio)" type="number" value={quantita} onChange={(e)=>{setQuantita(e.target.value);richiediRicalcoloSpeciale();}} />
            <label className="flex items-center gap-2"><input type="checkbox" checked={rateazione1422} onChange={(e)=>{setRateazione1422(e.target.checked);richiediRicalcoloSpeciale();}} />Rateazione</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={presunto1422} onChange={(e)=>{setPresunto1422(e.target.checked);richiediRicalcoloSpeciale();}} />Riduzione presunto</label>
            <Input label="Totale (€)" type="number" value={totale} onChange={(e)=>{totaleCalcoloModificatoRef.current=true;setTotale(e.target.value);}} />
          </>
        ) : isCalcoloBpoint1439 ? (
          <div className="col-span-2 rounded-md border border-purple-200 bg-purple-50/50 p-3">
            <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-purple-700">Calcolo rateazioni</div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {[
                ["Lavoratori subordinati", rateazioneSubordinati, setRateazioneSubordinati],
                ["Gestione Separata", rateazioneGestioneSeparata, setRateazioneGestioneSeparata],
                ["Commercianti / Artigiani", rateazioneAltraGestione, setRateazioneAltraGestione],
              ].map(([label, value, setter]) => (
                <label key={label} className="text-xs font-medium uppercase tracking-wider text-gray-500">
                  {label}
                  <select value={value} onChange={(e) => { totaleCalcoloModificatoRef.current = false; setter(e.target.value); }} className="mt-1 h-10 w-full rounded-md border border-gray-300 bg-white px-2 text-sm normal-case text-gray-800">
                    <option value="">Nessuna richiesta</option>
                    {BPOINT_1439_BANDS.filter((fascia) => fascia.id !== "NESSUNA").map((fascia) => <option key={fascia.id} value={fascia.id}>{fascia.name}</option>)}
                  </select>
                </label>
              ))}
              <Input label="Domande respinte non imputabili allo studio" type="number" min="0" step="1"
                value={rateazioneDomandeRespinte} onChange={(e) => { totaleCalcoloModificatoRef.current = false; setRateazioneDomandeRespinte(e.target.value); }} />
              <Input label="Totale proposto (€)" type="number" value={totale}
                onChange={(e) => { totaleCalcoloModificatoRef.current = true; setTotale(e.target.value); }} />
            </div>
            <div className="mt-2 text-xs text-gray-500">Minimo {fmt(prestazioneSel.importo_minimo)} €. Nessun tetto massimo. Il totale può essere modificato con il normale iter di validazione.</div>
          </div>
        ) : mostraQuantita ? (
          <>
            <div className="col-span-1 md:col-span-1">
              <Input label="Qtà" type="number" value={quantita} onChange={handleQtaChange} />
              {notaListino && notaVicinoQuantita ? (
                <div className="mt-1 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-900">
                  <span className="font-semibold">Nota:</span> {notaListino}
                </div>
              ) : null}
            </div>
            <div className="col-span-1 md:col-span-1">
              <Input
                label="Importo unit. (€)"
                type="number"
                value={importoUnit}
                onChange={handleImportoChange}
                disabled={lockImportiIipay || lockImportiAZero}
              />
              {notaListino && !notaVicinoQuantita ? (
                <div className="mt-1 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-900">
                  <span className="font-semibold">Nota:</span> {notaListino}
                </div>
              ) : null}
            </div>
            <div className="col-span-2 md:col-span-1">
              <Input
                label="Totale (€)"
                type="number"
              value={totale}
              onChange={(e) => setTotale(normalizeImportoSconto(prestazioneSel, e.target.value))}
              disabled
              />
            </div>
          </>
        ) : (
          <div className="col-span-2 md:col-span-1">
            <Input
              label="Totale (€)"
              type="number"
              value={totale}
              onChange={(e) => setTotale(normalizeImportoSconto(prestazioneSel, e.target.value))}
              disabled={lockImportiIipay || lockImportiAZero}
            />
            {notaListino ? (
              <div className="mt-1 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-900">
                <span className="font-semibold">Nota:</span> {notaListino}
              </div>
            ) : null}
          </div>
        )}
        {importoVariato && (
          <div className="col-span-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {isAdmin
              ? "Importo diverso dal listino."
              : "Importo diverso dal listino: la riga restera in bozza fino a conferma admin."}
          </div>
        )}
        <div className="col-span-2">
          <Input
            label={
              <span className="flex items-center gap-1.5">
                <span>
                  Descrizione aggiuntiva in fattura
                  {isRichiestaObbligatoria ? " *" : ""}
                </span>
                {richiestaHint && (
                  <span className="normal-case tracking-normal font-normal text-gray-400">→ {richiestaHint}</span>
                )}
              </span>
            }
            type="text"
            placeholder={isCalcoloIntegrazioneSalariale ? "Inserire periodo dal … al …" : undefined}
            value={descrizione}
            onChange={(e) => setDescrizione(e.target.value)}
          />
        </div>
        <div className="col-span-2">
          {includeMeseCompetenza ? (
            <div className="mb-2 rounded border border-blue-100 bg-blue-50 px-2 py-1 text-xs font-medium uppercase text-blue-700">
              Mese di competenza automatico da anagrafica cliente
            </div>
          ) : null}
        </div>
        <div className="col-span-2">
          <Input
            label="Note per Diego"
            type="text"
            value={notePerDiego}
            onChange={(e) => setNotePerDiego(e.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
};
